const FROZEN_PANE_COLUMN_ATTRIBUTE = 'data-xlsx-frozen-pane-column';
const FROZEN_PANE_COLUMN_BOUNDARY_ATTRIBUTE = 'data-xlsx-frozen-pane-column-boundary';
const FROZEN_PANE_ROW_ATTRIBUTE = 'data-xlsx-frozen-pane-row';
const FROZEN_PANE_ROW_BOUNDARY_ATTRIBUTE = 'data-xlsx-frozen-pane-row-boundary';
const CUSTOM_FREEZE_COLUMN_ATTRIBUTE = 'data-xlsx-custom-freeze-column';
const CUSTOM_FREEZE_ROW_ATTRIBUTE = 'data-xlsx-custom-freeze-row';
const FROZEN_PANE_ATTRIBUTES = [
  FROZEN_PANE_COLUMN_ATTRIBUTE,
  FROZEN_PANE_COLUMN_BOUNDARY_ATTRIBUTE,
  FROZEN_PANE_ROW_ATTRIBUTE,
  FROZEN_PANE_ROW_BOUNDARY_ATTRIBUTE,
] as const;
const FROZEN_PANE_STYLE_PROPERTIES = [
  '--xlsx-frozen-pane-background-color',
  '--xlsx-frozen-pane-column-offset',
  '--xlsx-frozen-pane-row-top',
] as const;

interface ApplySpreadsheetFreezePaneOptions {
  activeContent: HTMLDivElement;
  columnCellKey?: string;
  rowCellKey?: string;
}

const getCellByKey = (root: HTMLElement | null, cellKey: string | undefined) =>
  cellKey ? (root?.querySelector<HTMLElement>(`#${CSS.escape(`CELL-${cellKey}`)}`) ?? null) : null;

const getLogicalRowIndex = (row: HTMLTableRowElement): string | undefined =>
  row.querySelector<HTMLElement>('td[data-row-index]')?.dataset.rowIndex;

const getOpaqueBackgroundColor = (cell: HTMLElement): string => {
  const ownerWindow = cell.ownerDocument.defaultView ?? window;
  let element: HTMLElement | null = cell;

  while (element) {
    const { backgroundColor } = ownerWindow.getComputedStyle(element);
    if (backgroundColor && backgroundColor !== 'transparent' && backgroundColor !== 'rgba(0, 0, 0, 0)') {
      return backgroundColor;
    }
    element = element.parentElement;
  }

  return '#FFFFFF';
};

const clearSpreadsheetFreezePane = (activeContent: HTMLDivElement) => {
  activeContent.removeAttribute('data-xlsx-custom-freeze-pane');
  activeContent.removeAttribute(CUSTOM_FREEZE_COLUMN_ATTRIBUTE);
  activeContent.removeAttribute(CUSTOM_FREEZE_ROW_ATTRIBUTE);
  activeContent
    .querySelectorAll<HTMLElement>(FROZEN_PANE_ATTRIBUTES.map(attribute => `[${attribute}]`).join(','))
    .forEach(cell => {
      FROZEN_PANE_ATTRIBUTES.forEach(attribute => cell.removeAttribute(attribute));
      FROZEN_PANE_STYLE_PROPERTIES.forEach(property => cell.style.removeProperty(property));
    });
};

const markFrozenPaneCell = (cell: HTMLElement, attribute: string) => {
  cell.setAttribute(attribute, 'true');
  cell.style.setProperty('--xlsx-frozen-pane-background-color', getOpaqueBackgroundColor(cell));
};

export const applySpreadsheetFreezePane = ({
  activeContent,
  columnCellKey,
  rowCellKey,
}: ApplySpreadsheetFreezePaneOptions) => {
  clearSpreadsheetFreezePane(activeContent);

  const contentColumns = activeContent.querySelector<HTMLElement>('[id="spreadsheet-table__content-columns"]');
  if (!contentColumns || (!columnCellKey && !rowCellKey)) {
    return () => clearSpreadsheetFreezePane(activeContent);
  }

  activeContent.setAttribute('data-xlsx-custom-freeze-pane', 'true');
  const titleColumnsContainer = activeContent.querySelector<HTMLElement>('.titles-columns-container');

  if (rowCellKey) {
    const selectedCell = getCellByKey(contentColumns, rowCellKey) ?? getCellByKey(titleColumnsContainer, rowCellKey);
    const selectedRow = selectedCell?.closest('tr') as HTMLTableRowElement | null;
    const selectedLogicalRowIndex = selectedRow ? getLogicalRowIndex(selectedRow) : undefined;
    if (!selectedLogicalRowIndex) {
      return () => clearSpreadsheetFreezePane(activeContent);
    }

    const contentRows = Array.from(contentColumns.querySelectorAll<HTMLTableRowElement>('tr')).filter(
      (row): row is HTMLTableRowElement => row.tagName === 'TR' && Boolean(row.querySelector('td[data-row-index]'))
    );
    const selectedContentRowIndex = contentRows.findIndex(row => getLogicalRowIndex(row) === selectedLogicalRowIndex);
    if (selectedContentRowIndex === -1) {
      return () => clearSpreadsheetFreezePane(activeContent);
    }

    const selectedContentRow = contentRows[selectedContentRowIndex];
    activeContent.setAttribute(CUSTOM_FREEZE_ROW_ATTRIBUTE, 'true');
    const columnLegendHeight = contentColumns.querySelector<HTMLElement>('td.col-legend')?.offsetHeight ?? 0;
    const selectedTitleRow = titleColumnsContainer
      ?.querySelector<HTMLElement>(`td[data-row-index="${CSS.escape(selectedLogicalRowIndex)}"]`)
      ?.closest('tr') as HTMLTableRowElement | null;
    const frozenCells = [
      ...(Array.from(selectedContentRow.cells) as HTMLElement[]),
      ...(selectedTitleRow ? (Array.from(selectedTitleRow.cells) as HTMLElement[]) : []),
    ];

    frozenCells.forEach(cell => {
      markFrozenPaneCell(cell, FROZEN_PANE_ROW_ATTRIBUTE);
      cell.style.setProperty('--xlsx-frozen-pane-row-top', `${columnLegendHeight}px`);
      cell.setAttribute(FROZEN_PANE_ROW_BOUNDARY_ATTRIBUTE, 'true');
    });
  }

  if (columnCellKey) {
    const selectedCell = getCellByKey(contentColumns, columnCellKey);
    const selectedColumnLegend = selectedCell?.dataset.columnLegend;
    if (!selectedColumnLegend) {
      return () => clearSpreadsheetFreezePane(activeContent);
    }

    const selectedColumnLegendCell = contentColumns.querySelector<HTMLElement>(
      `td.col-legend[data-column-legend="${CSS.escape(selectedColumnLegend)}"]`
    );
    if (!selectedColumnLegendCell) {
      return () => clearSpreadsheetFreezePane(activeContent);
    }

    activeContent.setAttribute(CUSTOM_FREEZE_COLUMN_ATTRIBUTE, 'true');
    const matchingColumnCells = Array.from(
      contentColumns.querySelectorAll<HTMLElement>(`td[data-column-legend="${CSS.escape(selectedColumnLegend)}"]`)
    );
    matchingColumnCells.forEach(cell => {
      markFrozenPaneCell(cell, FROZEN_PANE_COLUMN_ATTRIBUTE);
      cell.style.setProperty('--xlsx-frozen-pane-column-offset', '0px');
      cell.setAttribute(FROZEN_PANE_COLUMN_BOUNDARY_ATTRIBUTE, 'true');
    });
  }

  return () => clearSpreadsheetFreezePane(activeContent);
};
